import { useState, useEffect, ReactElement } from 'react'
import { I18n } from 'common'
import MagnifyImageMessage from '../../products/MagnifyImageMessage'
import styles from './index.module.scss'
import { clsx } from 'clsx'

interface IImageZoomProps {
  zoomFactor?: number
  alt: string
  maxWidth?: string
  maxHeight?: string
  src: string
  id?: string
  className?: string
  onError?: (e: ErrorEvent) => void
  errorContent?: ReactElement
}

interface Coordinates {
  x: number
  y: number
}

enum Zoom {
  IN,
  OUT,
}

const ImageZoom = ({
  zoomFactor = 2,
  alt,
  src,
  id,
  className,
  onError,
  errorContent = (
    <p className={styles.errorText}>
      <I18n name="imageLoadError" />
    </p>
  ),
}: IImageZoomProps): ReactElement => {
  // define and set default values to the states of the component
  const [zoom, setZoom] = useState(Zoom.OUT)
  const [position, setPosition] = useState('50% 50%')
  const [imgData, setImgData] = useState<string>('')
  const [error, setError] = useState(false)
  // convert state data into strings to be used as helper classes
  const cssClasses = clsx([
    styles.figure,
    imgData && styles.figureLoaded,
    className,
  ])

  const getEventCoordinates = (e): Coordinates => {
    if (e.type === 'touchmove') {
      const touch = e.nativeEvent.touches[0] || e.nativeEvent.changedTouches[0]
      return { x: touch.pageX, y: touch.pageY }
    } else {
      return { x: e.clientX, y: e.clientY }
    }
  }

  const zoomInPosition = (e) => {
    // this will handle the calculations of the area where the image needs to zoom in depending on the user interaction
    const zoomer = e.currentTarget.getBoundingClientRect()
    const ePosition = getEventCoordinates(e)
    const x = ((ePosition.x - zoomer.x) / zoomer.width) * 100
    const y = ((ePosition.y - zoomer.y) / zoomer.height) * 100
    const position = `${x}% ${y}%`
    setPosition(position)
  }

  const toggleZoomImage = (e) => {
    if (zoom === Zoom.IN) {
      // zoom out
      setZoom(Zoom.OUT)
    } else {
      //zoom in and set the background position correctly
      setZoom(Zoom.IN)
      zoomInPosition(e)
    }
  }

  const handleMouseEnter = (e) => {
    // Handle the click events
    toggleZoomImage(e)
  }

  const handleMove = (e) => {
    // Handle the mouse move events
    if (zoom === Zoom.IN) {
      zoomInPosition(e)
    }
  }

  const handleLeave = () => {
    // Resets the state of the component on mouse leave
    setZoom(Zoom.OUT)
    setPosition('50% 50%')
  }

  useEffect(() => {
    // This checks if the prop src was passed when the component was called and throw an error if this is null or set to empty
    if (src === '' || src === null) {
      throw new Error(
        'Prop src must be defined when using ImageZoom component!'
      )
    }

    // Set initial state on component mount
    setZoom(Zoom.IN)
    const img = new Image()
    img.addEventListener('load', () => {
      // gracefully disable the loading animation
      setTimeout(() => {
        setZoom(Zoom.OUT)
        setImgData(img.src)
      }, 200)
    })
    img.addEventListener('error', (error) => {
      setError(true)
      if (onError) {
        onError(error) // call onError if image fails to load
      }
    })
    img.src = src
  }, [onError, src])

  if (error) {
    return errorContent
  } else {
    return (
      <div className={styles.magnifyImage}>
        <MagnifyImageMessage
          message={'rollOverImageToMagnify'}
          mobileMessage="tapAndRollOverImageToMagnify"
        />
        <figure
          id={id}
          className={cssClasses}
          style={{
            backgroundImage: `url( ${zoom === Zoom.IN ? imgData : ''} )`,
            backgroundSize: zoomFactor * 100 + '%',
            backgroundPosition: position,
            cursor: 'zoom-in',
            width: '100%',
          }}
          onMouseMove={handleMove}
          onMouseLeave={handleLeave}
          onMouseEnter={handleMouseEnter}
          onTouchStart={toggleZoomImage}
          onTouchMove={handleMove}
          onTouchEnd={toggleZoomImage}
        >
          <img
            id="imageZoom"
            className={styles.img}
            src={imgData}
            alt={alt}
            style={{
              opacity: zoom,
              width: '100%',
            }}
          />
        </figure>
      </div>
    )
  }
}

export default ImageZoom
